import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import FileWatermark, {
  FileWatermarkProps,
} from 'components/FileWatermark/FileWatermark.component';

import './AutodeskViewer.styles.scss';

type AutodeskViewerProps = {
  className?: string;
  urn: string;
  accessToken: string;
  onDocumentLoad?: () => void;
} & FileWatermarkProps;

const AutodeskViewer: React.FC<AutodeskViewerProps> = (props) => {
  const {
    className,
    urn,
    accessToken,
    fullName,
    email,
    ipAddress,
    watermarkSettings,
    onDocumentLoad,
  } = props;

  const [isLoaded, setIsLoaded] = useState(false);

  const { popError } = useContext(PopNotificationsContext);

  const classes = classNames('autodesk-viewer', className);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Autodesk.Viewing.GuiViewer3D | null>(null);

  const handleDocumentReady = useCallback(() => {
    setIsLoaded(true);
    onDocumentLoad?.();
  }, [onDocumentLoad]);

  useEffect(() => {
    if (!containerRef.current || !urn || !accessToken) return;

    let cancelled = false;

    const initViewer = () => {
      const options: Autodesk.Viewing.InitializerOptions = {
        env: 'AutodeskProduction2',
        api: 'streamingV2',
        getAccessToken: (onSuccess) => {
          onSuccess(accessToken, 3600);
        },
      };

      Autodesk.Viewing.Initializer(options, () => {
        if (cancelled || !containerRef.current) return;

        const viewer = new Autodesk.Viewing.GuiViewer3D(containerRef.current);
        viewer.start();
        viewerRef.current = viewer;

        Autodesk.Viewing.Document.load(
          `urn:${urn}`,
          (doc) => {
            if (cancelled) return;

            const root = doc.getRoot();
            const viewables3d = root.search({ role: '3d' });
            const viewable =
              viewables3d.length > 0
                ? viewables3d[0]
                : root.getDefaultGeometry();
            viewer.loadDocumentNode(doc, viewable);
            handleDocumentReady();
          },
          (_errorCode, _errorMsg) => {
            popError(`Autodesk document load failed: ${_errorMsg}`);
          },
        );
      });
    };

    initViewer();

    return () => {
      cancelled = true;

      if (viewerRef.current) {
        viewerRef.current.finish();
        viewerRef.current = null;
      }
    };
  }, [urn, accessToken, popError, handleDocumentReady]);

  return (
    <div className={classes} ref={containerRef}>
      {isLoaded && (
        <FileWatermark
          fullName={fullName}
          email={email}
          ipAddress={ipAddress}
          watermarkSettings={watermarkSettings}
        />
      )}
    </div>
  );
};

export default AutodeskViewer;
